import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

import { headingFontFamily } from "../heading-font";
import { COMPANY } from "./company";

/**
 * The closing "how to reach us" block shared by every legal page. Authored as:
 *
 * ```mdx
 * <LegalContact security>
 *   If you have any questions or concerns about this security information please contact us.
 * </LegalContact>
 * ```
 *
 * `children` is the page's own intro line; the heading and the registered
 * address come from here so there is one copy of them. `security` adds the
 * vulnerability-reports address alongside the general one.
 */
export function LegalContact({
	children,
	security = false,
}: {
	children?: ReactNode;
	security?: boolean;
}) {
	return (
		<Box
			component="section"
			sx={{ py: 4 }}>
			<Typography
				component="h2"
				sx={{
					fontFamily: headingFontFamily,
					fontSize: "1.75rem",
					fontWeight: 600,
					lineHeight: 1.3,
					mt: 6,
					mb: 2,
				}}>
				How can you contact us about this notice?
			</Typography>

			{children}

			<Typography sx={{ fontSize: "1.0625rem", lineHeight: 1.7 }}>
				{security ? (
					<>
						Security reports and questions:{" "}
						<Link href={`mailto:${COMPANY.securityEmail}`}>
							{COMPANY.securityEmail}
						</Link>
						<br />
						General queries:{" "}
						<Link href={`mailto:${COMPANY.supportEmail}`}>
							{COMPANY.supportEmail}
						</Link>
					</>
				) : (
					<Link href={`mailto:${COMPANY.supportEmail}`}>
						{COMPANY.supportEmail}
					</Link>
				)}
				{COMPANY.addressLines.map((line) => (
					<Box
						key={line}
						component="span">
						<br />
						{line}
					</Box>
				))}
			</Typography>
		</Box>
	);
}
